import { action, computed, makeObservable, observable } from 'mobx';

import { integration, setupRequestInterceptor } from '../services';
import { AuthInfo, TokenInfo } from '../models';
import { FrontErrorCodes } from '../enums';
import { authController } from '../api';
import { jitter } from '../helpers';
import { RootStore } from '.';
import { failLaunch, markLaunchStep, setLaunchAuthToken } from '../services/launch-telemetry';

/**
 * Delay before each retry of a failed refresh, then stop. Our own refresh failing is a hub-features problem, not a
 * player problem (the game reports its own session errors), so we retry quietly. But a refresh token the server has
 * rejected stays rejected, and re-sending it every 60 s for the life of the session doubled the Token/refresh 401s
 * (17,961 -> 34,162 per 4 h on 09-30). Five answers from the server over ~15 min, jittered, then the hub stays in
 * A3 hub-down mode until a relaunch, the only thing that can mint a new token anyway. A request that never reached
 * the server says nothing about the token, so network failures back off along the ladder but never exhaust it.
 */
const REFRESH_RETRY_LADDER_MS = [60_000, 120_000, 240_000, 480_000];

// A shorter expiry than the insurance would otherwise schedule a negative timeout, i.e. a hot refresh loop.
const MIN_REFRESH_DELAY_MS = 10_000;

export class AuthStore {

  private rootStore: RootStore;

  public authInfo?: AuthInfo;

  private expirationInsurance = 100; // 100 seconds

  // The one pending refresh, whether the regular pre-expiry one or a backoff retry; see scheduleRefresh.
  private refreshTokenTimer: NodeJS.Timeout | null = null;

  // Absolute expiry (epoch ms) of the current access token; 0 = unknown. Lets the SignalR
  // accessTokenFactory refresh on demand instead of relying on the throttle-prone setTimeout.
  private accessTokenExpiresAt = 0;

  // De-dupes concurrent refreshes (reconnect attempts can call getFreshAccessToken at the same time).
  private refreshPromise: Promise<void> | null = null;

  // Consecutive failed refreshes from any caller (timer or on demand); indexes the retry ladder. Not observable,
  // like the rest of the refresh bookkeeping. Reset by a fresh token in handleAuthInfoUpdate.
  private refreshFailures = 0;

  // The server rejected the refresh token after the whole ladder: no caller may send it again before a relaunch.
  private refreshExhausted = false;

  /**
   * True once our own refresh has failed, cleared when one succeeds. Not observable: only the launcher
   * socket reads it, to tell a live session from a stale token. refresh() leaves authInfo in place on
   * failure, so the presence of an access token says nothing on its own.
   */
  public refreshFailed = false;

  /**
   * False for cookie-based authorization, where the launch carries no tokenInfo and the cookie
   * authenticates the socket. Token checks are meaningless then — there is nothing for us to hold.
   */
  public usesTokenAuth = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      authInfo: observable,
      setAuthInfo: action.bound,
      isAuth: computed
    });
  }

  public get isAuth(): boolean {
    return !!this.rootStore?.gameStore.launchData.data?.isAuthenticated;
  }

  public setAuthInfo(value: AuthInfo): void {
    this.authInfo = value;
  }

  public initAuthFlow(tokenInfo: TokenInfo | null, expiresIn: number): void {
    // No tokenInfo on an authenticated launch means cookie-based authorization, not a broken launch:
    // the cookie authenticates the socket and there is no token for us to hold or inspect.
    this.usesTokenAuth = !!tokenInfo;
    if (tokenInfo) {
      this.handleAuthInfoUpdate({ ...tokenInfo, expiresIn });
    }
    this.attachRefreshTimer(expiresIn);
    markLaunchStep('auth');
  }

  public handleAuthInfoUpdate(value: AuthInfo): void {
    this.setAuthInfo(value);
    // A fresh token, from a successful refresh or a new launch, ends the failure streak.
    this.refreshFailed = false;
    this.refreshFailures = 0;
    this.refreshExhausted = false;
    // Remember when this token actually dies so the socket can refresh on demand (not just on the timer).
    this.accessTokenExpiresAt = value.expiresIn ? Date.now() + value.expiresIn * 1000 : 0;
    // Push the new token to the frames already open; updateAuthInfo reads it from authInfo, set above.
    integration.current?.updateAuthInfo(this.rootStore.gameStore.launchData.data);
    if (value.accessToken) {
      setupRequestInterceptor(value.accessToken);
      setLaunchAuthToken(value.accessToken);
    }
  }

  public async refresh(): Promise<void> {
    const response = await authController.refresh(this.authInfo?.refreshToken);
    // skipInterceptor hands back the raw envelope, and http-service substitutes the default one when the request
    // never got an HTTP response. `?.` is cheap insurance: a throw here used to kill the whole retry chain, because
    // the timer path discards this promise and the on-demand path swallows it.
    const authInfo = response?.result;
    if (authInfo) {
      this.handleAuthInfoUpdate(authInfo);
      this.attachRefreshTimer(authInfo.expiresIn);
      return;
    }
    // Our refresh failing is not proof the player's session ended. The game reports the operator token
    // error itself (GameErrorCodes.SessionExpired over postMessage); until then the game keeps running.
    this.refreshFailed = true;
    failLaunch('refresh_token_error', FrontErrorCodes.RefreshTokenError);
    // Only retry when there is something to retry with. Cookie-based launches hold no refresh token,
    // so re-arming here would poll Token/refresh, per player, for the life of the session.
    if (!this.authInfo?.refreshToken) return;
    this.refreshFailures += 1;
    // Only an answer from the server proves the token is dead; a request that never reached it says nothing.
    if (!response?.noResponse && this.refreshFailures > REFRESH_RETRY_LADDER_MS.length) {
      // Ladder spent: this token is dead until a relaunch. refreshFailed stays true for the launcher socket.
      this.refreshExhausted = true;
      this.clearRefreshTimer();
      return;
    }
    const rung = Math.min(this.refreshFailures, REFRESH_RETRY_LADDER_MS.length) - 1;
    this.scheduleRefresh(jitter(REFRESH_RETRY_LADDER_MS[rung]));
  }

  // Called by the SignalR accessTokenFactory on every (re)connect. Reads the LIVE store and refreshes
  // on demand, so a reconnect never carries a stale/expired token (the cause of the rejected-token
  // dropped wins). Returns '' only if there is genuinely no token (fun-mode, cookie auth). After a failed
  // refresh it returns the stale token we still hold; the launcher socket reads refreshFailed to tell.
  public async getFreshAccessToken(): Promise<string> {
    // Fun-mode / unauthenticated launches have no refresh token — return '' as before and do NOT
    // attempt a refresh: it would fail for a player who never had a session.
    if (!this.authInfo?.refreshToken) {
      return this.authInfo?.accessToken || '';
    }
    // Once the ladder is spent a reconnect must not POST the dead refresh token again: the launcher socket's
    // 5-minute anonymous lane evaluates this twice per pass, for the life of the session.
    if (this.isAccessTokenExpiring() && !this.refreshExhausted) {
      try {
        await this.ensureRefreshed();
      } catch {
        // refresh() records its own failure and schedules (or stops) its own retry; nothing to do here.
      }
    }
    return this.authInfo?.accessToken || '';
  }

  private isAccessTokenExpiring(): boolean {
    if (!this.authInfo?.accessToken) return true;            // nothing to send -> try to fetch one
    if (!this.accessTokenExpiresAt) return false;            // unknown TTL -> assume valid
    return Date.now() >= this.accessTokenExpiresAt - this.expirationInsurance * 1000;
  }

  // Collapse concurrent refreshes (reconnect storms) into a single in-flight request.
  private ensureRefreshed(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.refresh().finally(() => { this.refreshPromise = null; });
    }
    return this.refreshPromise;
  }

  public async getRefToken(): Promise<void> {
    this.rootStore.slotHistoryStore.setRefTokenInfo(this.rootStore.slotHistoryStore.refTokenInfo.fetching());
    const data = await authController.getRefToken();
    const accessToken  = data.accessToken || '';
    this.rootStore.slotHistoryStore.setRefTokenInfo(this.rootStore.slotHistoryStore.refTokenInfo.fetched(accessToken));
  }

  public attachRefreshTimer(expiresIn: number): void {
    this.scheduleRefresh(Math.max((expiresIn - this.expirationInsurance) * 1000, MIN_REFRESH_DELAY_MS));
  }

  // The single timer slot, in ms. Runs through ensureRefreshed so a timer refresh joins an on-demand one already in
  // flight instead of doubling it; the catch keeps a surprise from surfacing as js_unhandled_rejection.
  private scheduleRefresh(delayMs: number): void {
    this.clearRefreshTimer();
    this.refreshTokenTimer = setTimeout(() => {
      this.ensureRefreshed().catch(() => undefined);
    }, delayMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTokenTimer) {
      clearTimeout(this.refreshTokenTimer);
      this.refreshTokenTimer = null;
    }
  }

}
